import { differenceInDays } from 'date-fns';
import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useProfiles } from '../../contexts/ProfileContext';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { getFormattedUsername, priceFormatterV2 } from '@/lib/utils';
import { Crm } from '@/services/CrmService';

interface ProfileItemProps {
  crm: Crm;
}

export function ProfileItem({ crm }: ProfileItemProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Profiles.components.ProfileList',
  });

  const { user } = useAuth();

  const { viewCrm } = useProfiles();

  const today = new Date();
  const lastDate = crm?.lastPurchaseDate
    ? new Date(crm.lastPurchaseDate)
    : new Date();

  const daysPassed = differenceInDays(today, lastDate);

  return (
    <li className="flex items-center gap-[25px] border-b px-5 py-[14px]">
      <div>
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-sm font-medium text-white">
          {getFormattedUsername(crm.name, crm.email, crm.userProfileId).initial}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="w-[200px] truncate text-sm">
                {
                  getFormattedUsername(crm.name, crm.email, crm.userProfileId)
                    .displayName
                }
              </span>
            </TooltipTrigger>
            <TooltipContent className="bg-gray-900 p-4">
              <span className="text-sm">
                {
                  getFormattedUsername(crm.name, crm.email, crm.userProfileId)
                    .displayName
                }
              </span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <span className="text-xs text-muted-foreground">
          {t('daysSinceLastPurchase', { day: daysPassed })}
        </span>
      </div>

      <div className="flex w-[200px] flex-col gap-2">
        {crm.totalSpent && (
          <span className="text-sm">
            {t('totalSpent', {
              value: priceFormatterV2(
                crm.totalSpent,
                user!.customer.language,
                user!.customer.platform,
              ),
            })}
          </span>
        )}

        {crm.totalProductsPurchased && (
          <span className="text-xs text-muted-foreground">
            {t('totalProductsPurchased', { count: crm.totalProductsPurchased })}
          </span>
        )}
      </div>

      <ul className="flex flex-1 flex-wrap gap-1">
        {crm.audienceUser?.slice(0, 3)?.map((audienceUser) => (
          <li key={audienceUser.audienceId}>
            <span className="rounded-full bg-gray-100 px-[10px] py-1 text-xs text-muted-foreground">
              {audienceUser.audience.name}
            </span>
          </li>
        ))}

        {(crm.audienceUser?.length ?? 0) > 3 && (
          <li>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="rounded-full bg-gray-100 px-[10px] py-1 text-xs text-muted-foreground">
                    ...
                  </button>
                </TooltipTrigger>

                <TooltipContent className="bg-gray-900 p-4">
                  <ul className="flex max-w-[600px] flex-wrap gap-4">
                    {crm.audienceUser?.map((audienceUser) => (
                      <li key={audienceUser.audienceId}>
                        <span className="rounded-full bg-gray-100 px-[10px] py-1 text-xs text-muted-foreground">
                          {audienceUser.audience.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </li>
        )}
      </ul>

      <button onClick={() => viewCrm(crm.id)}>
        <Eye className="h-6 w-6" />
      </button>
    </li>
  );
}
